import type { RelatedRecordType } from "../../Project/types/project-documents.types.ts"
import type { FileFormatCategory } from "../helpers/classify-file-format.ts"
import type { DateRangePreset } from "../helpers/date-range-filter.ts"

/** "all" is never sent as a param — it's the same as the filter being unset. */
type OriginFilter = "customer" | "team"

interface FilesFilters {
  // Classified client-side; never sent as a query param.
  file_type: FileFormatCategory | null
  // Legacy Attachment generic-FK parent filter; no drawer control sets it any more.
  model_type: string | null
  date_range: DateRangePreset | null
  date_after: string | null
  date_before: string | null
  folder: string | null
  folder_include_subfolders: boolean
  album: string | null
  owner: string | null
  document_type: string | null
  // A document's workflow status; `null` matches everything.
  status: string | null
  // Label is captured at selection time; no static list resolves an id back to one.
  related_record_type: RelatedRecordType | null
  related_record_id: string | null
  related_record_label: string | null
  // Origin, read server-side off who actually uploaded/created the row,
  // never a stored/derived flag a later move or reclassification could
  // drift from or accidentally clear. "Uploaded by" applies to files
  // (documents/photos); "Created by" applies to containers
  // (folders/albums) — a team-created album holding customer uploads is
  // not itself customer-created, so these are deliberately two separate
  // controls, not one.
  uploaded_by_origin: OriginFilter | null
  created_by_origin: OriginFilter | null
  // Customer `id`s from the searchable multi-select — narrows either
  // origin filter above to specific customers, scoped to customers
  // authorized on a project this caller can see.
  customers: string[]
}

const EMPTY_FILES_FILTERS: FilesFilters = {
  file_type: null,
  model_type: null,
  date_range: null,
  date_after: null,
  date_before: null,
  folder: null,
  folder_include_subfolders: true,
  album: null,
  owner: null,
  document_type: null,
  status: null,
  related_record_type: null,
  related_record_id: null,
  related_record_label: null,
  uploaded_by_origin: null,
  created_by_origin: null,
  customers: []
}

const MEANINGFUL_FILTER_KEYS: Array<keyof FilesFilters> = [
  "file_type",
  "model_type",
  "date_range",
  "folder",
  "album",
  "owner",
  "document_type",
  "status",
  "related_record_id",
  "uploaded_by_origin",
  "created_by_origin",
  "customers"
]

/** The filters currently narrowing the Files result. */
const activeFilterKeys = (filters: FilesFilters): Array<keyof FilesFilters> =>
  MEANINGFUL_FILTER_KEYS.filter((key) => {
    const value = filters[key]
    return Array.isArray(value) ? value.length > 0 : value !== null
  })

const countActiveFilters = (filters: FilesFilters): number =>
  activeFilterKeys(filters).length

type FilesSortOrder =
  | "modified_desc"
  | "added_desc"
  | "document_date_desc"
  | "document_date_asc"
  | "name_asc"
  | "name_desc"
  | "size_desc"
  | "size_asc"

const DEFAULT_SORT_ORDER: FilesSortOrder = "modified_desc"

export {
  activeFilterKeys,
  countActiveFilters,
  DEFAULT_SORT_ORDER,
  EMPTY_FILES_FILTERS
}
export type { FilesFilters, FilesSortOrder, OriginFilter }
